import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  FocusEvent,
  ChangeEvent,
} from 'react';

import { DEFAULT_MAXIMUM_FRACTION_DIGITS } from '../consts';
import {
  createNumericValidationRegex,
  escapeRegexCharacters,
} from '../helpers';
import {
  SignDisplayType,
  TrailingZeroDisplayType,
  NumericInputChangeEvent,
} from '../types';

export type UseNumericInputProps = {
  value?: number | null;
  defaultValue?: number | null;
  allowNegative?: boolean;
  locale?: string | string[];
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  maximumSignificantDigits?: number;
  trailingZeroDisplay?: TrailingZeroDisplayType;
  signDisplay?: SignDisplayType;
  useGrouping?: boolean;
  onChange?: (event: NumericInputChangeEvent) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
};

export default ({
  value,
  defaultValue,
  allowNegative = false,
  locale,
  minimumFractionDigits,
  maximumFractionDigits,
  maximumSignificantDigits,
  trailingZeroDisplay = 'auto',
  signDisplay = 'auto',
  useGrouping = false,
  onChange,
  onBlur,
}: UseNumericInputProps) => {
  const defaultProcessed = useRef<boolean>(false);

  const parseNumber = (
    value: string,
    groupingSeparator: string,
    decimalSeparator: string,
  ): number =>
    parseFloat(
      value.replaceAll(groupingSeparator, '').replace(decimalSeparator, '.'),
    );

  const numDecimals =
    typeof maximumFractionDigits === 'number'
      ? Math.max(maximumFractionDigits, minimumFractionDigits ?? 0)
      : Math.max(DEFAULT_MAXIMUM_FRACTION_DIGITS, minimumFractionDigits ?? 0);

  const formatter = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        style: 'decimal',
        minimumFractionDigits,
        maximumFractionDigits:
          typeof maximumFractionDigits === 'number' &&
          typeof minimumFractionDigits === 'number'
            ? Math.max(maximumFractionDigits, minimumFractionDigits)
            : maximumFractionDigits,
        maximumSignificantDigits,
        signDisplay,
        numberingSystem: 'latn',
        useGrouping,
      }),
    [
      locale,
      minimumFractionDigits,
      maximumFractionDigits,
      maximumSignificantDigits,
      signDisplay,
      useGrouping,
    ],
  );

  const [groupingSeparator, decimalSeparator] = useMemo(() => {
    const parts = new Intl.NumberFormat(locale, {
      style: 'decimal',
      useGrouping,
      numberingSystem: 'latn',
    }).formatToParts(99999999.9999);
    const grouping = parts.find((part) => part.type === 'group')?.value ?? '';
    const decimal = parts.find((part) => part.type === 'decimal')?.value ?? '.';
    return [grouping, decimal];
  }, [locale, useGrouping]);

  const validationRegex = useMemo(
    () =>
      createNumericValidationRegex({
        groupingSeparator,
        decimalSeparator,
        allowNegative,
        maxDecimalPlaces: numDecimals,
      }),
    [allowNegative, numDecimals, groupingSeparator, decimalSeparator],
  );

  const trailingZeroRegex = useMemo(() => {
    if (trailingZeroDisplay === 'stripIfInteger') {
      return new RegExp(
        '[' + escapeRegexCharacters(decimalSeparator) + ']0+$',
        'g',
      );
    }
    return null;
  }, [trailingZeroDisplay, decimalSeparator]);

  const formatNumber = useCallback(
    (value: number) => {
      const formatted = formatter.format(value).replace('\u2212', '-');
      if (trailingZeroRegex) {
        return formatted.replace(trailingZeroRegex, '');
      }
      return formatted;
    },
    [formatter, trailingZeroRegex],
  );

  const [stringValue, setStringValue] = useState<string>(() => {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return '';
    }
    return formatNumber(value);
  });

  useLayoutEffect(() => {
    const actualValue =
      value === undefined && !defaultProcessed.current ? defaultValue : value;
    defaultProcessed.current = true;
    if (actualValue !== undefined) {
      const parsedValue = parseNumber(
        stringValue,
        groupingSeparator,
        decimalSeparator,
      );
      if (typeof actualValue === 'number' && !Number.isNaN(actualValue)) {
        if (
          parsedValue !== actualValue &&
          (!Number.isNaN(parsedValue) || actualValue !== 0)
        ) {
          setStringValue(formatNumber(actualValue));
        }
      } else if (actualValue === null || Number.isNaN(actualValue)) {
        if (!Number.isNaN(parsedValue)) {
          setStringValue('');
        }
      }
    }
  }, [
    defaultValue,
    value,
    stringValue,
    formatNumber,
    groupingSeparator,
    decimalSeparator,
  ]);

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      const parsedValue = parseNumber(
        stringValue,
        groupingSeparator,
        decimalSeparator,
      );
      if (Number.isNaN(parsedValue)) {
        if (stringValue !== '') {
          setStringValue(formatNumber(0));
        }
      } else {
        setStringValue(formatNumber(parsedValue));
      }
      if (onBlur) {
        onBlur(event);
      }
    },
    [stringValue, groupingSeparator, decimalSeparator, onBlur, formatNumber],
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      const isValidInput = validationRegex.test(newValue);
      if (!isValidInput) {
        return;
      }

      setStringValue(newValue);

      const parsedValue = parseNumber(
        newValue,
        groupingSeparator,
        decimalSeparator,
      );
      if (onChange) {
        if (newValue === '') {
          onChange({ target: { ...event.target, value: null } });
        } else if (Number.isNaN(parsedValue)) {
          onChange({ target: { ...event.target, value: 0 } });
        } else {
          onChange({ target: { ...event.target, value: parsedValue } });
        }
      }
    },
    [validationRegex, groupingSeparator, decimalSeparator, onChange],
  );

  return {
    stringValue,
    handleBlur,
    handleChange,
  };
};
